import { ApiError, isValidImage } from "@gorila-shared-ui/components";
import { FILE_TYPE_DOCUMENT, FILE_TYPE_IMAGE } from "../constants/app";
import { FileType, PaginatedResponse } from "../types/app";
import {
  Job,
  JobCore,
  JobFilters,
  JobScheduleRequest,
  JobStatusItem,
  Skill,
  Worker,
} from "../types/jobs";
import { getRequest, postRequest } from "./api";
import { URLS } from "./urls/urls";

export const getJobs = async (
  page: number = 1,
  search: string = "",
  jobFilters?: JobFilters,
  projection: string = "full",
): Promise<{
  paginatedResponse?: PaginatedResponse<JobCore>;
  error?: ApiError;
}> => {
  const params = new URLSearchParams();
  params.set("page", page.toString());
  params.set("search", search);
  params.set("projection", projection);
  Object.entries(jobFilters || {}).forEach(([key, value]) => {
    if (key === "jobStatus") return;
    if (value !== undefined && value !== null) {
      if (typeof value === "object") {
        const id = value["_id"];
        if (id) {
          params.set(key, id?.toString());
        }
        return;
      }
      params.set(key, value.toString());
    }
  });
  if (Array.isArray(jobFilters?.jobStatus)) {
    jobFilters.jobStatus.forEach((status) => {
      params.append("jobStatus", status.toString());
    });
  }
  const queryString = params.toString();

  const response = await getRequest(`${URLS.jobs.all}?${queryString}`);
  return {
    paginatedResponse: response?.data,
    error: response?.error,
  };
};

export const getJob = async (
  jobId: string,
): Promise<{
  job?: Job;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.detail(jobId));
  return {
    job: response?.data,
    error: response?.error,
  };
};

export const updateJobSchedule = async (
  jobId: string,
  jobSchedule: JobScheduleRequest,
): Promise<{
  error?: ApiError;
}> => {
  const response = await postRequest(URLS.jobs.updateSchedule(jobId), {
    body: jobSchedule,
  });
  return {
    error: response?.error,
  };
};

export const getJobSkills = async (
  page: number = 1,
  search: string = "",
  projection: string = "full",
): Promise<{
  paginatedResponse?: PaginatedResponse<Skill>;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.skills.all, {
    queryParams: { page, search, projection },
  });
  return {
    paginatedResponse: response?.data,
    error: response?.error,
  };
};

export const createJobSkill = async (
  skill: Skill,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.skills.create, {
    body: skill,
  });
  return {
    error: response?.error,
  };
};

export const updateJobSkill = async (
  skillSlug: string,
  skill: Skill,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.skills.update(skillSlug), {
    body: skill,
  });
  return {
    error: response?.error,
  };
};

export const getJobStatusList = async (): Promise<{
  jobStatusList?: JobStatusItem[];
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.statusList);
  return {
    jobStatusList: response?.data,
    error: response?.error,
  };
};

export const getJobActivitiesFiles = async (
  jobId: string,
  fileKey: string,
): Promise<{
  url?: string;
  type?: FileType;
  error?: ApiError;
}> => {
  const response = await postRequest(URLS.jobs.activity.file(jobId), {
    body: { fileKey },
  });
  return {
    url: response?.data?.url,
    type: isValidImage(fileKey) ? FILE_TYPE_IMAGE : FILE_TYPE_DOCUMENT,
    error: response?.error,
  };
};

export const getJobPdfFile = async (
  jobId: string,
): Promise<{
  url?: string;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.pdf.download(jobId));
  return {
    url: response?.data?.url,
    error: response?.error,
  };
};

export const generateJobPdfFile = async (
  jobId: string,
): Promise<{
  msg?: string;
  error?: ApiError;
}> => {
  const response = await postRequest(URLS.jobs.pdf.generate(jobId));
  return {
    msg: response?.data?.msg,
    error: response?.error,
  };
};

export const updateJobOverprice = async (
  jobId: string,
  overprice: number,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.update(jobId), {
    body: {
      overprice,
    },
  });
  return {
    error: response?.error,
  };
};

export const updateJobExpenses = async (
  jobId: string,
  expenses: number,
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.update(jobId), {
    body: {
      expenses,
    },
  });
  return {
    error: response?.error,
  };
};

export const getWorkersList = async (
  tenant: string,
  scheduleDate: number,
  jobType: string,
): Promise<{
  workers?: Worker[];
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.workers, {
    queryParams: {
      tenant,
      scheduleDate: new Date(scheduleDate).getTime(),
      jobType,
    },
  });
  return {
    workers: response?.data,
    error: response?.error,
  };
};

export const cancelJob = async (
  jobId: string,
): Promise<{
  error?: ApiError;
}> => {
  const response = await postRequest(URLS.jobs.cancel(jobId));
  return {
    error: response?.error,
  };
};

export const cancelJobWithFailedVisit = async (
  jobId: string,
  reason: string,
): Promise<{
  msg?: string;
  error?: ApiError;
}> => {
  const response = await postRequest(URLS.jobs.failedVisit(jobId), {
    body: { reason },
  });
  return {
    error: response?.error,
  };
};
